import React from 'react';
import { Tabs, Typography } from 'antd';
import SeriesPasteEditor from '../SeriesPasteEditor';
import { IntegrityChartPreview } from '../PagePreview';
import RichField from '../RichField';
import { Help, Panel, S } from '../ui';

const { Text } = Typography;

interface Props {
  source: any;
  patch: (updater: (next: any) => void) => void;
  derived: any;
}

/**
 * The integrity index: two series, on two timeframes.
 *
 * Quarterly and annual are tabs rather than two stacked tables. They hold the same two
 * columns and are rarely edited together -- a month's update touches the newest row of
 * each -- so showing both at once cost a lot of vertical space and made the page feel
 * like a form to be filled rather than a figure to be checked.
 *
 * The chart sits under the tabs, outside them, because it draws BOTH timeframes: it is
 * the thing the numbers are for, and hiding it behind a tab would mean switching tabs to
 * see the effect of a change.
 *
 * The summary comes last, under the chart. It was a step of its own, which meant writing
 * about a movement two screens after seeing it, from memory. The words carry the numbers
 * -- "eased to 7.9 in Q3" -- and those numbers change every month, so the chart they
 * describe needs to be on the same screen as the sentence describing it.
 */
const StepIntegrity: React.FC<Props> = ({ source, patch, derived }) => {
  const q = source?.integrity?.quarterly ?? { periods: [], issuance: [], retirement: [] };
  const a = source?.integrity?.annual ?? { periods: [], issuance: [], retirement: [] };

  const setBlock = (key: 'quarterly' | 'annual') => (periods: string[], columns: number[][]) =>
    patch((next) => {
      next.integrity[key] = { periods, issuance: columns[0], retirement: columns[1] };
    });

  return (
    <div>
      <Help>
        Computed by the platform and brought in by step 1. Correct anything that looks
        wrong directly in the table; the chart below redraws as you type.
      </Help>

      <Tabs
        defaultActiveKey="quarterly"
        items={[
          {
            key: 'quarterly',
            label: `Quarterly (${q.periods?.length ?? 0})`,
            children: (
              <SeriesPasteEditor
                title="Quarterly figures"
                help="Quarter by quarter."
                placeholder={'2026-Q1\t3.8\t5.7\n2026-Q2\t8.1\t4.7'}
                kind="quarterly"
                valueLabels={['issuance', 'retirement']}
                periods={q.periods ?? []}
                columns={[q.issuance ?? [], q.retirement ?? []]}
                onChange={setBlock('quarterly')}
              />
            ),
          },
          {
            key: 'annual',
            label: `Annual (${a.periods?.length ?? 0})`,
            children: (
              <SeriesPasteEditor
                title="Annual figures"
                help="Year by year. The YTD marker comes from the date in step 1, not from anything here."
                placeholder={'2025\t5.0\t4.7\n2026\t7.0\t5.5'}
                kind="annual"
                valueLabels={['issuance', 'retirement']}
                periods={a.periods ?? []}
                columns={[a.issuance ?? [], a.retirement ?? []]}
                onChange={setBlock('annual')}
              />
            ),
          },
        ]}
      />

      <Panel title="How the chart will look">
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: S.sm }}>
          Both timeframes, as the page draws them. The dashed tail marks a period that has
          not finished by the date in step 1.
        </Text>
        <IntegrityChartPreview derived={derived} />
      </Panel>

      <RichField
        label="Summary beside the integrity chart"
        help="Issuance and retirement: what moved this period, and why. It sits next to the chart above on the published page."
        editorKey={`integrity-${source?.as_of ?? 'blank'}`}
        value={source?.commentary?.integrity ?? ''}
        onChange={(html) => patch((next) => { next.commentary.integrity = html; })}
      />
    </div>
  );
};

export default StepIntegrity;
